// Zero-dep hash router (react-router-dom is 403-blocked by the private registry).
// Supports: <Router>, useRoute(), navigate(), <Link>, path params like "/places/:id".
import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const RouteCtx = createContext({ path: '/', params: {} });

function currentPath() {
  const h = window.location.hash.replace(/^#/, '');
  return h || '/';
}

export function navigate(to) {
  window.location.hash = to;
}

export function Router({ children }) {
  const [path, setPath] = useState(currentPath());
  useEffect(() => {
    const onHash = () => setPath(currentPath());
    window.addEventListener('hashchange', onHash);
    if (!window.location.hash) window.location.hash = '/';
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  return <RouteCtx.Provider value={{ path }}>{children}</RouteCtx.Provider>;
}

// Match "/places/:id" against "/places/42" -> { id: "42" } or null.
function match(pattern, path) {
  const pp = pattern.split('/').filter(Boolean);
  const ap = path.split('/').filter(Boolean);
  if (pp.length !== ap.length) return null;
  const params = {};
  for (let i = 0; i < pp.length; i++) {
    if (pp[i].startsWith(':')) params[pp[i].slice(1)] = decodeURIComponent(ap[i]);
    else if (pp[i] !== ap[i]) return null;
  }
  return params;
}

export function useRoute() {
  return useContext(RouteCtx);
}

// routes: [{ path, element }]. First match wins; `fallback` used if none match.
export function useMatchedRoute(routes, fallback) {
  const { path } = useRoute();
  for (const r of routes) {
    const params = match(r.path, path);
    if (params) return { element: r.element, params };
  }
  return { element: fallback, params: {} };
}

export function Link({ to, children, className, onClick }) {
  const handle = useCallback(
    (e) => {
      e.preventDefault();
      if (onClick) onClick(e);
      navigate(to);
    },
    [to, onClick]
  );
  return (
    <a href={`#${to}`} className={className} onClick={handle}>
      {children}
    </a>
  );
}
